// Section quản trị CƠ QUAN (SCR-050 tab "Cơ quan"): adapter map DTO generated-schema sang
// AdminWorkspace generic. API object khai báo module-level để tham chiếu ổn định (effect deps).

import { App as AntdApp } from "antd";
import {
  closeOrganization,
  createOrganization,
  getOrganizationDetail,
  getOrganizationHistory,
  getOrganizationTree,
  mergeOrganization,
  openOrganization,
  renameOrganization,
  transferOrganization,
  type OrganizationDetail,
  type OrganizationTreeNode,
  type OrganizationVersionView,
} from "./organizationAdminApi";
import type { AdminCommandOutcome, AdminNode, AdminDetail, AdminVersion, AdminWorkspaceApi } from "./adminTypes";
import { AdminWorkspace } from "./components/AdminWorkspace";

function toNode(node: OrganizationTreeNode): AdminNode {
  return {
    id: node.id,
    code: node.code,
    name: node.name,
    classification: node.type,
    parentId: node.parentId ?? null,
    status: node.status,
  };
}

function toDetail(detail: OrganizationDetail): AdminDetail {
  return { ...toNode(detail), validFrom: detail.validFrom, validTo: detail.validTo ?? null, version: detail.version };
}

function toVersion(version: OrganizationVersionView): AdminVersion {
  return {
    version: version.version,
    name: version.name,
    classification: version.type,
    parentId: version.parentId ?? null,
    status: version.status,
    validFrom: version.validFrom,
    validTo: version.validTo ?? null,
  };
}

function toOutcome(outcome: { id: string; version: number; rehomedChildIds?: string[] | undefined }): AdminCommandOutcome {
  return { id: outcome.id, version: outcome.version, rehomedChildIds: outcome.rehomedChildIds };
}

/** exactOptionalPropertyTypes: chỉ gửi `reason` khi có giá trị (request DTO khai báo `reason?: string`). */
function reasonField(reason: string | undefined): { reason?: string } {
  return reason != null ? { reason } : {};
}

const organizationAdminApi: AdminWorkspaceApi = {
  getTree: (signal) => getOrganizationTree(undefined, signal).then((nodes) => nodes.map(toNode)),
  getDetail: (id, signal) => getOrganizationDetail(id, undefined, signal).then(toDetail),
  getHistory: (id, signal) => getOrganizationHistory(id, signal).then((versions) => versions.map(toVersion)),
  create: (input, key) =>
    createOrganization(
      {
        code: input.code,
        name: input.name,
        type: input.classification,
        parentId: input.parentId ?? null,
        validFrom: input.validFrom,
        reason: input.reason,
      },
      key,
    ).then(toOutcome),
  rename: (input, key) =>
    renameOrganization(
      input.id,
      {
        expectedVersion: input.expectedVersion,
        newName: input.newName,
        effectiveFrom: input.effectiveFrom,
        decisionReference: input.decisionReference,
        ...reasonField(input.reason),
      },
      key,
    ).then(toOutcome),
  transfer: (input, key) =>
    transferOrganization(
      input.id,
      {
        expectedVersion: input.expectedVersion,
        newParentId: input.newParentId,
        effectiveFrom: input.effectiveFrom,
        decisionReference: input.decisionReference,
        ...reasonField(input.reason),
      },
      key,
    ).then(toOutcome),
  merge: (input, key) =>
    mergeOrganization(
      input.survivorId,
      {
        expectedVersion: input.survivorExpectedVersion,
        absorbedId: input.absorbedId,
        absorbedExpectedVersion: input.absorbedExpectedVersion,
        effectiveFrom: input.effectiveFrom,
        decisionReference: input.decisionReference,
        ...reasonField(input.reason),
      },
      key,
    ).then(toOutcome),
  close: (input, key) =>
    closeOrganization(
      input.id,
      {
        expectedVersion: input.expectedVersion,
        effectiveFrom: input.effectiveFrom,
        decisionReference: input.decisionReference,
        ...reasonField(input.reason),
      },
      key,
    ).then(toOutcome),
  open: (input, key) =>
    openOrganization(input.id, { expectedVersion: input.expectedVersion, effectiveFrom: input.effectiveFrom, reason: input.reason }, key).then(
      toOutcome,
    ),
};

export function OrganizationAdminSection() {
  return (
    <AntdApp>
      <AdminWorkspace entityNoun="tổ chức" classificationLabel="Loại tổ chức" api={organizationAdminApi} />
    </AntdApp>
  );
}
